import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Popover, Box, Typography, Grid, Skeleton,
} from "@mui/material";
import {
  setExtraProductData, setIsBackToHoreca, setQuantities,
  setSelectedExtraProduct, updateReservationGroup,
  updateReservationType, updateTime,
} from "../../store/slice/Addguest.slice";
import { getExtraProductDetails } from "../../api/HorecaWidget.service";
import { format_Number } from "../../utils/utils";
import { useWidgetHeight } from "./widgetHeightContext";
import { UsePopoverPosition } from "../../hooks/UsePopoverPosition";

const ReservationType = ({
  Ref, isOpen, handleClose, isLoading,
  isCombination = false, lang, Id,
}) => {
  const { css }    = useSelector((store) => store?.widgetSettings);
  const restypeCss = css?.find((item) => item?.cssTabFieldsGroupName === "Reservation type")?.fields;

  const [imageStatus, setImageStatus] = useState({});

  const {
    totalCount, reservationTypeDatas, reservType, time, reservGroup,
    selectedExtraProduct, quantities, isBackToHoreca,
    languageId, defaultLanguage, languageWords,
  } = useSelector((store) => store?.addGuest);

  const { widgetConfig } = useSelector((store) => store?.widgetSettings);
  const { defaultLang }  = useSelector((store) => store?.Combination);
  const dispatch = useDispatch();
  const { onPopoverClose } = useWidgetHeight();

  // ── Shared positioning (identical behaviour to EventComponent) ────────────
  const {
    matchesSm,
    anchorOriginVert,
    transformOriginVert,
    paperSx,
    mobileOverrideProps,
  } = UsePopoverPosition(Ref, isOpen, 400);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchExtraProducts = async () => {
    try {
      const response = await getExtraProductDetails(
        reservType?.idreservationtype, defaultLang || defaultLanguage,
      );
      if (response?.status !== 200 || !response?.data) throw new Error("fetch failed");
      dispatch(setExtraProductData(response?.data));
    } catch (error) {
      console.error("error extra product", error?.message);
    }
  };

  useEffect(() => {
    if (reservType?.idreservationtype != null && !isBackToHoreca) fetchExtraProducts();
  }, [reservType?.idreservationtype, isBackToHoreca, defaultLanguage]);

  useEffect(() => {
    if (
      reservationTypeDatas?.length === 1 &&
      Object.keys(reservType).length === 0
    ) {
      dispatch(updateReservationType(reservationTypeDatas[0]));
      localStorage?.setItem("idreservation", reservationTypeDatas[0]?.idreservationtype);
    }
  }, [reservationTypeDatas]);

  // ── Selection handler ─────────────────────────────────────────────────────
  const handleSelect = (item) => {
    dispatch(setIsBackToHoreca(false));
    if (selectedExtraProduct?.length > 0)          dispatch(setSelectedExtraProduct([]));
    if (Object.keys(quantities || {}).length > 0)  dispatch(setQuantities({}));
    if (reservGroup && Object.keys(reservGroup).length > 0) dispatch(updateReservationGroup({}));
    if (time?.trim() !== "")                       dispatch(updateTime(""));
    localStorage?.setItem("idreservation", item?.idreservationtype);
    dispatch(updateReservationType(item));
    handleClose();
    onPopoverClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Popover
      open={isOpen}
      anchorEl={Ref?.current}
      onClose={() => { handleClose(); onPopoverClose(); }}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      {...mobileOverrideProps}        
      PaperProps={{
        sx: {
          ...paperSx,                
          backgroundColor: restypeCss?.ReservationTypesBackgoundColor,
        }
      }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock={false}
    >
      <Box
        sx={{
          backgroundColor: restypeCss?.ReservationTypesBackgoundColor,
          p: { xs: 1.5, sm: 3 },
          pb: { xs: 3, sm: 3 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
        }}
      >
        {isLoading ? (
          Array.from(new Array(2)).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "0.5rem",
                backgroundColor: "#EBEBEB",
                border: "1px solid #EBEBEB",
                p: 1,
                borderRadius: "8px",
                width: "100%",
              }}
            >
              <Grid container>
                <Grid item xs={12} md={4}>
                  <Skeleton variant="rectangular" height={110} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ p: 1, width: "100%" }}>
                    <Skeleton />
                    <Skeleton width="60%" />
                    <Skeleton width="80%" />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))
        ) : reservationTypeDatas?.length <= 0 ? (
          <Typography variant="body2" sx={{ textAlign: "center", color: "grey" }}>
            No reservation type available.
          </Typography>
        ) : (
          reservationTypeDatas?.map((item, i) => {
            const ID = isCombination ? Id : languageId;
            const nameTranslation = item.reservationtypeLanguages.find(
              (l) => l.fieldname === "name" || l.idlanguage === ID,
            );
            const infoTranslation = item.reservationtypeLanguages.find(
              (l) => l.fieldname === "info" || l.idlanguage === ID,
            );
            const isActiveItem = reservType?.name === item?.name;
            const imgLoaded    = imageStatus[item.idreservationtype] === true;
            const imgFailed    = imageStatus[item.idreservationtype] === false;
            const showImage    = item?.imageFileName && !imgFailed;

            return (
              <Box
                key={i}
                onClick={() => handleSelect(item)}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: "0.5rem",
                  backgroundColor: isActiveItem
                    ? restypeCss?.ReservationTypesItemBackgroundColorActive
                    : restypeCss?.ReservationTypesItemBackgroundColor,
                  border: `1px solid ${restypeCss?.ReservationTypesItemBorderColor}`,
                  color: isActiveItem
                    ? restypeCss?.ReservationTypesItemTextColorActive ?? "#fff"
                    : restypeCss?.ReservationTypesItemTextColor ?? "#000",
                  p: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  width: "100%",
                  transition: "all 0.3s ease-in-out",
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                  "&:hover": {
                    backgroundColor: restypeCss?.ReservationTypesItemBackgroundColorHover,
                    color: restypeCss?.ReservationTypesItemTextColorHover,
                    transform: "scale(1.02)",
                    boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
                  },
                  "@media (hover: none)": {
                    "&:hover": { transform: "none", boxShadow: "none" },
                    "&:active": { opacity: 0.85 },
                  },
                }}
              >
                <Grid container spacing={1}>
                  {showImage && (
                    <Grid item xs={12} sm={4} md={4}>
                      <Box
                        sx={{
                          width: "100%",
                          height: { xs: 180, sm: "100%" },
                          minHeight: { sm: 120 },
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        }}
                      >
                        <img
                          src={item?.imageFileName}
                          alt="res-type-img"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            transition: "transform 0.3s ease",
                          }}
                          onLoad={() =>
                            setImageStatus((p) => ({ ...p, [item.idreservationtype]: true }))
                          }
                          onError={() =>
                            setImageStatus((p) => ({ ...p, [item.idreservationtype]: false }))
                          }
                        />
                      </Box>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12}
                    sm={showImage && imgLoaded ? 8 : 12}
                    md={showImage && imgLoaded ? 8 : 12}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pt: { xs: showImage ? 0.5 : 0, sm: 0 },
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", fontWeight: "600", mb: 1 }}>
                        {nameTranslation?.data}
                      </Typography>
                      {item?.totalCost > 0 &&
                        totalCount < widgetConfig?.reservationRequestMin && (
                          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                            €{format_Number(parseFloat(item?.totalCost).toFixed(2))}
                          </Typography>
                        )}
                    </Box>

                    {infoTranslation?.data && (
                      <Typography
                        sx={{
                          fontSize: matchesSm ? "13px" : "0.9rem",
                          lineHeight: 1.5,
                        }}
                      >
                        {infoTranslation?.data}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            );
          })
        )}
      </Box>
    </Popover>
  );
};

export default ReservationType;
